import { addToMap, getDiffProps } from "@/utils/common"
import {
  beforeSubmit,
  decorateSheet,
  fillRefVal,
  getAllControlMap,
  getControlDefaultVal,
  getRules,
  transCalcValRelyMap,
  transControlValRelyMap
} from "@/utils/sheet"
import { dealSheetRule, getCalcRes } from "@/api/sheet/sheet"
import _ from "lodash"

export default class FormUtil {
  dataId = null
  snStep = 1

  sheetInfo = {} // 原始表结构
  decoratedSheet = []
  controlMap = {}
  allControlMap = {}
  refControlCidMap = {}
  defaultValRelyMap = {}
  calcValRelyMap = {}
  fieldRuleCidList = []
  fillFields = []

  sheetDefUpdateCbk = null
  updateFormFunc = null
  formGetterFunc = null

  isCreate = false
  rules = {}

  constructor({
    dataId,
    sheetInfo,
    availableSheets,
    snStep,
    updateFormFunc,
    fillFields,
    formGetterFunc
  }) {
    this.dataId = dataId
    this.snStep = snStep || 1
    this.isCreate = !dataId
    this.availableSheets = availableSheets
    this.fillFields = fillFields || []
    this.allControlMap = getAllControlMap(availableSheets) // 减少内存使用
    this.updateFormFunc = updateFormFunc
    this.formGetterFunc = formGetterFunc
    this.updateSheet(sheetInfo)
  }
  updateSheet(sheetInfo) {
    this.sheetInfo = Object.assign(this.sheetInfo, sheetInfo) // 更新的时候用Object.assign,避免覆盖layout
    this.decoratedSheet = decorateSheet(sheetInfo) || {}

    let fillControlMap = _.keyBy(this.fillFields, "_cid")
    let controls = (this.decoratedSheet.controls || []).map(control => {
      let fillControl = fillControlMap[control._cid]
      if (fillControl) {
        return Object.assign({}, control, {
          required: fillControl.required || control.required,
          disabled: fillControl.disabled || control.disabled
        })
      }
      return Object.assign({}, control)
    })
    this.rules = getRules(controls, this.formGetterFunc, this.allControlMap, this.sheetInfo.sheetId)
    this.controlMap = _.keyBy(controls, "_cid")
    this.calcValRelyMap = transCalcValRelyMap(controls, this.isCreate ? [] : ["sn"])
    this.defaultValRelyMap = transControlValRelyMap(controls)
    this.refControlCidMap = this.getRefControlCidMap(sheetInfo)
    this.fieldRuleCidList = this.getFieldRuleCidList(sheetInfo)
    this.sheetDefUpdateCbk?.(this.sheetInfo)
  }
  getRefControlCidMap(sheetInfo) {
    let map = {}
    sheetInfo.controls
      .filter(control => control._type === "field")
      .forEach(control =>
        addToMap(map, control.refCid, { cid: control._cid, refField: control.field })
      )
    return map
  }
  // [字段规则] cid数组，这些字段变化需向服务器请求更新当前的controls
  // +所有option类型是动态数据源的控件中,被设置为筛选条件的控件
  getFieldRuleCidList(sheetInfo) {
    let optionVarCidArr = sheetInfo.controls
      .filter(
        control =>
          control._type === "option" &&
          control.optionType === "动态数据源" &&
          control.refSources.filters
      )
      .map(control => control.refSources.filters || [])
      .flatMap(filters => _.flattenDeep(filters))
      .flatMap(filter => filter.varItems)
      .filter(item => item.var)
      .map(item => item.value)
    return _.chain(sheetInfo.rules || [])
      .map(rule => rule.filters.map(filter => filter.map(item => item.key)))
      .concat(optionVarCidArr)
      .flattenDeep()
      .uniq()
      .value()
  }
  async initForCreate(form, updateObj = {}, group) {
    // 初始化所有不依赖于其他字段的字段默认值
    let dependentCids = new Set(Object.values(this.defaultValRelyMap).flat())
    await Promise.all(
      this.decoratedSheet.controls
        .filter(control => !dependentCids.has(control._cid) && !_.isEmpty(control.defaultVal))
        .map(control => getControlDefaultVal(control).then(val => (updateObj[control._cid] = val)))
    )
    // 看板分组新建
    if (!_.isEmpty(group)) {
      switch (group.type) {
        case "option":
          if (group.value !== "未分组") {
            updateObj[group.fieldId] = [group.value]
          }
          break
        case "rate":
          if (group.value !== 0) {
            updateObj[group.fieldId] = group.value
          }
          break
      }
    }
    // sn控件初始化值
    await this.calcValueByServer(
      this.decoratedSheet.controls
        .filter(control => control._type === "sn")
        .map(control => control._cid),
      updateObj
    )
    await this.handleFieldUpdate({
      form,
      updateObj,
      isInitial: true
    })
  }
  async initForCopy(form, initData) {
    let updateObj = _.cloneDeep(initData) || {}
    // sn控件初始化值
    await this.calcValueByServer(
      this.decoratedSheet.controls
        .filter(control => control._type === "sn")
        .map(control => control._cid),
      updateObj
    )
    await this.handleFieldUpdate({
      form,
      updateObj,
      isInitial: true
    })
  }
  /**
   * 处理 form 分组中的某个字段值发生变化 / 和表单数据初始化
   * @param form
   * @param updateObj
   * @param key
   * @param isInitial 表单数据初始化 时需传入,会把所有更改的字段全部更新到控件中(控件值主动变更的情况不需要此操作)
   * @param isFromSubTable 是否由从表触发（由任一从表触发的更改，不去刷新任一从表）
   * @returns {Promise<void>}
   * @private
   */
  async handleFieldUpdate({ form, updateObj, isInitial = false, isFromSubTable = false }) {
    if (_.isEmpty(updateObj)) return
    this.updateFormFunc({ updateObj, isFromSubTable })
    let oldForm = _.cloneDeep(form)
    let newForm = Object.assign({}, form, updateObj)
    let triggerCids = Object.keys(updateObj)

    await this.updateRefField({ newForm, oldForm })
    await this.updateDefaultVal({ newForm, oldForm, triggerCids })
    await this.updateRefField({ newForm, oldForm }) // 默认值里面也可能有关联表，所以再更新一下（临时处理）
    await this.triggerUpdate(newForm, oldForm, isInitial ? [] : triggerCids)
    let updatedForm = _.cloneDeep(newForm)

    await this.updateCalcVal({ newForm, oldForm })
    await this.triggerUpdate(newForm, updatedForm, isInitial ? [] : triggerCids)

    if (this.hasChangedInRules(getDiffProps(newForm, oldForm)))
      return this.handleFieldRules(newForm)
  }
  async triggerUpdate(newForm, oldForm, ignoredCids) {
    let updateObj = _.pick(
      newForm,
      getDiffProps(newForm, oldForm).filter(cid => !ignoredCids.includes(cid))
    )
    if (_.isEmpty(updateObj)) return
    this.updateFormFunc({ updateObj, initFromGroup: true })
  }
  // 更新关联表字段
  async updateRefField({ newForm, oldForm }) {
    let updateCids = getDiffProps(newForm, oldForm)
    updateCids.forEach(updateCid => {
      let sheetData = newForm[updateCid]?.[0]
      let fieldItems = this.refControlCidMap[updateCid] || []
      fieldItems.forEach(item => {
        newForm[item.cid] = sheetData?.data?.[item.refField]
      })
    })
    await this.fillControlVal({ newForm, oldForm })
  }
  async fillControlVal({ newForm, oldForm }) {
    // 为控件填充数据
    return Promise.all(
      getDiffProps(newForm, oldForm)
        .map(cid => this.allControlMap[cid])
        .filter(({ _type }) => _type === "ref")
        .map(c => fillRefVal(c, newForm[c._cid]).then(v => (newForm[c._cid] = v)))
    )
  }
  // 更新字段的默认值
  async updateDefaultVal({ newForm, oldForm, triggerCids }) {
    let tempForm = oldForm
    let needUpdateFields = []
    // eslint-disable-next-line no-constant-condition
    while (true) {
      needUpdateFields = this.getNeedUpdateFields(
        getDiffProps(newForm, tempForm),
        this.defaultValRelyMap
      ).filter(cid => !triggerCids.includes(cid))
      if (!needUpdateFields.length) break
      tempForm = _.cloneDeep(newForm)
      await Promise.all(
        needUpdateFields.map(
          async cid =>
            (newForm[cid] = await getControlDefaultVal(
              this.controlMap[cid],
              newForm,
              this.allControlMap
            ))
        )
      )
    }
  }
  // 更新计算、自动编码和汇总字段
  async updateCalcVal({ newForm, oldForm }) {
    let updatedCids = getDiffProps(newForm, oldForm)
    let needUpdateFields = []
    // 当前修改的字段可能会造成多层依赖的更新,此处循环处理
    while (updatedCids.length) {
      updatedCids = this.getNeedUpdateFields(updatedCids, this.calcValRelyMap).filter(
        cid => !needUpdateFields.includes(cid)
      )
      needUpdateFields.push(...updatedCids)
    }
    // 通过服务器更新
    await this.calcValueByServer(needUpdateFields, newForm)
  }
  async calcValueByServer(cids, form) {
    if (_.isEmpty(cids)) return
    let sheetData = await getCalcRes(
      this.getSheetDataServer(form, this.dataId, true),
      this.snStep || 1
    )
    cids.forEach(cid => (form[cid] = sheetData.data[cid]))
  }
  // 改变的字段是否包括字段规则里的字段
  hasChangedInRules(updatedCids) {
    return _.intersection(this.fieldRuleCidList, updatedCids).length
  }
  // 处理字段规则里的字段改变
  handleFieldRules(newForm) {
    return dealSheetRule(this.sheetInfo.sheetId, this.getSheetDataServer(newForm, this.dataId))
      .then(({ data: sheetDef }) => this.updateSheet(sheetDef))
      .catch(() => this.$message.error("请求出错，请刷新重试！"))
  }
  // 获取需要更新的字段cid (用于defaultVal、calcVal)
  getNeedUpdateFields(diffFields, relyMap) {
    let relyMapKeys = Object.keys(relyMap)
    let needUpdateFields = diffFields.reduce((acc, cid) => {
      relyMapKeys.forEach(key => {
        if (key.startsWith(cid)) acc = acc.concat(relyMap[key])
      })
      return acc
    }, [])
    needUpdateFields = Array.from(new Set(needUpdateFields))
    return needUpdateFields
  }
  getSheetDataServer(form, id = null, skipRelatedControl = true) {
    return {
      id,
      sheetId: this.sheetInfo.sheetId,
      data: beforeSubmit({ form, fieldsMap: this.controlMap, skipRelatedControl })
    }
  }
}
